import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from 'lucide-react';
import { Customer } from '../data/mockData';
import { sendDocumentReminder, canSendReminder } from '../services/dataService';
import { format, formatDistanceToNow } from 'date-fns';
import { useToast } from '../context/ToastContext';
import { useDemoMode } from '../context/DemoModeContext';
import { useAuth } from '../context/AuthContext';

interface ReminderButtonProps {
  customer: Customer;
  onReminderSent: (updatedCustomer: Customer) => void;
  compact?: boolean;
}

const ReminderButton = ({ customer, onReminderSent, compact = false }: ReminderButtonProps) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { isDemoMode } = useDemoMode();
  const { currentUser } = useAuth();
  const [sending, setSending] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [success, setSuccess] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  // Check if we can send a reminder
  const canSend = canSendReminder(customer);
  
  // Calculate completed documents
  const completedRequired = customer.requiredDocuments.filter(req => 
    customer.uploadedDocuments.some(doc => doc.requirementId === req.id)
  ).length;
  
  const allCompleted = completedRequired === customer.requiredDocuments.length;
  
  // Don't show button if all documents are complete
  if (allCompleted) return null;
  
  // Get a human-readable last reminder time
  const getLastReminderText = () => {
    if (!customer.lastReminderSent) return null;
    
    const lastReminderDate = new Date(customer.lastReminderSent);
    return t('lastReminderSent', { 
      timeAgo: formatDistanceToNow(lastReminderDate, { addSuffix: true }) 
    });
  };

  // Get tooltip text for disabled button
  const getTooltipText = () => {
    if (!customer.lastReminderSent) return '';
    
    return t('reminderCooldown', { 
      date: format(new Date(customer.lastReminderSent), 'MMM d, yyyy') 
    });
  };
  
  const handleSendReminder = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigating if this is in a link
    e.stopPropagation(); // Prevent event bubbling
    
    if (!canSend || sending) return;
    
    setSending(true);
    try {
      const updatedCustomer = await sendDocumentReminder(customer, isDemoMode);
      e.preventDefault();
      
      // Show toast notification based on demo mode
      if (isDemoMode) {
        showToast(
          `Email would have been sent to ${customer.name}, but since this is a demo, no actual email was sent.`,
          'warning',
          6000
        );
      } else {
        showToast(
          `Reminder email sent successfully to ${customer.name}!`,
          'success',
          4000
        );
      }
      // Log reminder to Recent Activity
      const sentRequests = JSON.parse(localStorage.getItem('sentRequests') || '[]');
      sentRequests.push({
        id: `${Date.now()}-${Math.random()}`,
        templateName: t('documentReminder'),
        recipientEmail: customer.email, // Use email address instead of name
        sentAt: new Date().toISOString(),
        status: 'sent',
        documentCount: customer.requiredDocuments.length,
        actionType: 'reminder', // Mark as reminder
        sentBy: currentUser?.username || t('unknown') // Use username, not display name
      });
      localStorage.setItem('sentRequests', JSON.stringify(sentRequests));
      
      // Show success message briefly
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
      
      // Update the customer data in the parent component
      onReminderSent(updatedCustomer);
    } catch (error) {
      console.error('Error sending reminder:', error);
      showToast('Failed to send reminder email. Please try again.', 'error', 5000);
    } finally {
      setSending(false);
    }
  };
  
  if (compact) {
    // Compact version for table cell
    return (
      <div className="flex flex-col mt-1 items-center">
        <button
          ref={buttonRef}
          onClick={handleSendReminder}
          onMouseEnter={() => !canSend && setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          disabled={!canSend || sending}
          className={`px-2 py-1 text-xs rounded-full flex items-center justify-center ${
            canSend 
              ? 'bg-blue-500 hover:bg-blue-600 text-white' 
              : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
          }`}
          aria-label={t('sendReminderEmailBtnText')}
        >
          {sending ? (
            <Loader size={12} className="animate-spin mr-1" />
          ) : success ? (
            <span className="text-green-50">✓</span>
          ) : (
            <span>{t('sendReminderEmailBtnText')}</span>
          )}
        </button>
        
        {showTooltip && !canSend && (
          <div className="absolute z-10 mt-2 -ml-2 px-3 py-2 rounded bg-gray-800 text-white text-xs w-48 shadow-lg">
            {getTooltipText()}
          </div>
        )}
        
        {customer.lastReminderSent && (
          <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
            Reminder last sent: {format(new Date(customer.lastReminderSent), 'M/d/yy')}
          </span>
        )}
      </div>
    );
  }
  
  // Full version for detail page
  return (
    <div className="flex flex-col">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleSendReminder}
        onMouseEnter={() => !canSend && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        disabled={!canSend || sending}
        className={`flex items-center justify-center px-4 py-2 rounded-full text-sm font-medium ${
          canSend 
            ? 'bg-blue-500 hover:bg-blue-600 text-white' 
            : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
        }`}
        aria-label={t('sendReminderEmailBtnText')}
      >
        {sending ? (
          <>
            <Loader size={16} className="mr-2 animate-spin" />
            {t('sendingReminder')}
            | Test
          </>
        ) : success ? (
          <span>{t('reminderSuccess')} ✓</span>
        ) : (
          <>{t('sendReminderEmailBtnText')} ✉️</>
        )}
      </button>
      
      {showTooltip && !canSend && (
        <div className="absolute z-10 mt-12 px-4 py-2 rounded bg-gray-800 text-white text-sm shadow-lg max-w-xs">
          {getTooltipText()}
        </div>
      )}
      
      {customer.lastReminderSent && (
        <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {getLastReminderText()}
        </span>
      )}
    </div>
  );
};

export default ReminderButton;
